/**
 * Column Quality Component
 * Shows data quality metrics, validation rules, and quality scores
 */
import React, { useState } from 'react';
import { Box, Typography, Paper, Chip, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface ColumnQualityProps {
  columnName: string;
}

// Circular Progress Component
const CircularProgressWithLabel: React.FC<{ value: number; label: string; size?: number }> = ({ value, label, size = 140 }) => {
  const getColor = (val: number) => {
    if (val >= 90) return '#2e7d32';
    if (val >= 70) return '#ed6c02';
    return '#d32f2f';
  };

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={size}
          thickness={4}
          sx={{ color: 'divider', position: 'absolute' }}
        />
        <CircularProgress
          variant="determinate"
          value={value}
          size={size}
          thickness={4}
          sx={{ color: getColor(value) }}
        />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="h4" component="div" sx={{ fontWeight: 700, color: getColor(value) }}>
            {value}
          </Typography>
        </Box>
      </Box>
      <Typography variant="body2" sx={{ mt: 1, fontWeight: 600, color: '#666' }}>
        {label}
      </Typography>
    </Box>
  );
};

const ColumnQuality: React.FC<ColumnQualityProps> = ({ columnName }) => {
  const [expandedRule, setExpandedRule] = useState<number | null>(null);

  // Quality rules data
  const qualityRules = [
    {
      element: `${columnName}`,
      domain: 'DQ Rule~FINANCIALS~...',
      type: 'Data Quality Rule',
      score: 100,
      dimensions: ['Accuracy', 'Completeness']
    },
    {
      element: `${columnName}_Data`,
      domain: 'DQ Rule~FINANCIALS~...',
      type: 'Data Quality Rule',
      score: 100,
      dimensions: ['Accuracy', 'Completeness']
    },
    {
      element: `${columnName}_Null`,
      domain: 'DQ Rule~FINANCIALS~...',
      type: 'Data Quality Rule',
      score: 100,
      dimensions: ['Accuracy', 'Completeness']
    },
    {
      element: `${columnName}_Unique`,
      domain: 'DQ Rule~FINANCIALS~...',
      type: 'Data Quality Rule',
      score: 100,
      dimensions: ['Accuracy', 'Completeness']
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Circular Progress Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {/* Overview Card */}
        <Paper 
          sx={{ 
            p: 3, 
            flex: '1 1 200px',
            minWidth: 200,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            border: '1px solid #e0e0e0'
          }}
        >
          <CircularProgressWithLabel value={100} label="Overview" />
        </Paper>

        {/* Accuracy Card */}
        <Paper 
          sx={{ 
            p: 3, 
            flex: '1 1 200px',
            minWidth: 200,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            border: '1px solid #e0e0e0'
          }}
        >
          <CircularProgressWithLabel value={100} label="Accuracy" />
        </Paper>

        {/* Completeness Card */}
        <Paper 
          sx={{ 
            p: 3, 
            flex: '1 1 200px',
            minWidth: 200,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            border: '1px solid #e0e0e0'
          }}
        >
          <CircularProgressWithLabel value={100} label="Completeness" />
          <IconButton 
            size="small" 
            sx={{ 
              mt: 1,
              bgcolor: '#1976d2',
              color: 'white',
              '&:hover': { bgcolor: '#1565c0' },
              width: 28,
              height: 28
            }}
          >
            <AddIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Paper>
      </Box>

      {/* Details Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Details
        </Typography>
        <IconButton 
          size="small" 
          sx={{ 
            bgcolor: '#1976d2',
            color: 'white',
            '&:hover': { bgcolor: '#1565c0' },
            width: 24,
            height: 24
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* Data Quality Rules Table */}
      <TableContainer component={Paper} sx={{ border: '1px solid #e0e0e0' }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: '12px', color: '#666' }}>Data Element</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '12px', color: '#666' }}>Domain</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '12px', color: '#666' }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '12px', color: '#666' }}>Quality Score</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '12px', color: '#666' }}>Dimensions</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '12px', color: '#666', width: 50 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {qualityRules.map((rule, index) => (
              <TableRow 
                key={index}
                sx={{ 
                  '&:hover': { bgcolor: '#f5f5f5' },
                  cursor: 'pointer'
                }}
              >
                <TableCell>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#1976d2',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                      fontSize: '13px'
                    }}
                  >
                    {rule.element}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: '13px', color: '#666' }}>
                    {rule.domain}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: '13px', color: '#666' }}>
                    {rule.type}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 600, color: '#2e7d32' }}>
                    {rule.score}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {rule.dimensions.map((dim, i) => (
                      <Chip 
                        key={i}
                        label={dim} 
                        size="small" 
                        sx={{ 
                          height: 20,
                          fontSize: '11px',
                          bgcolor: '#e3f2fd',
                          color: '#1976d2'
                        }} 
                      />
                    ))}
                  </Box>
                </TableCell>
                <TableCell>
                  <IconButton size="small">
                    <InfoOutlinedIcon sx={{ fontSize: 18, color: '#666' }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Info */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, alignItems: 'center', gap: 2 }}>
        <Typography variant="caption" color="text.secondary">
          1-4 of 4
        </Typography>
      </Box>
    </Box>
  );
};

export default ColumnQuality;
